import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { TIERS } from './AncestralProject';

/**
 * Single-tier printable budget page.
 * Auto-triggers window.print() on load so it saves straight to PDF.
 */
export default function BudgetPrint() {
  const { tierId } = useParams();
  const tier = TIERS.find((t) => t.id === tierId);

  useEffect(() => {
    if (!tier) return;
    document.title = `Ancestors Awaken — ${tier.amountLabel} Budget`;
    const t = setTimeout(() => window.print(), 800);
    return () => clearTimeout(t);
  }, [tier]);

  if (!tier) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <p>Tier not found.</p>
        <Link to="/ancestral-project" style={{ color: '#eab308' }}>← Back to proposal</Link>
      </div>
    );
  }

  const total = tier.budget.reduce((s, l) => s + l.amount, 0);

  return (
    <div
      style={{
        background: 'white',
        color: 'black',
        fontFamily: 'Georgia, "Times New Roman", serif',
        minHeight: '100vh',
        padding: '0.5in',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      }}
    >
      <style>{`
        @media print {
          @page { size: letter portrait; margin: 0.5in; }
          .no-print { display: none !important; }
          html, body { background: white !important; }
        }
        .row { display: flex; justify-content: space-between; padding: 8pt 0; border-bottom: 1px solid #e5e5e5; font-size: 11pt; }
        .row:last-child { border-bottom: none; }
        .amt { font-weight: 700; font-variant-numeric: tabular-nums; }
      `}</style>

      {/* On-screen controls (hidden on print) */}
      <div className="no-print" style={{ marginBottom: 24, display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/ancestral-project" style={{ color: '#eab308', fontWeight: 700, textDecoration: 'none' }}>← Back to proposal</Link>
        <button
          onClick={() => window.print()}
          style={{ padding: '8pt 16pt', background: '#eab308', color: 'black', border: 'none', borderRadius: 999, fontWeight: 800, cursor: 'pointer' }}
        >
          📥 Save as PDF
        </button>
      </div>

      {/* Header */}
      <div style={{ borderBottom: '3px solid #eab308', paddingBottom: 12, marginBottom: 20 }}>
        <p style={{ margin: 0, fontSize: '9pt', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#a16207' }}>
          Casa Samba · NOTCF Grant Proposal
        </p>
        <h1 style={{ margin: '4pt 0 0 0', fontSize: '26pt', fontWeight: 900, lineHeight: 1.15 }}>Ancestors Awaken</h1>
        <p style={{ margin: '2pt 0 0 0', fontStyle: 'italic', fontSize: '11pt', color: '#525252' }}>
          An Afro-Diasporic Musical Convergence
        </p>
      </div>

      {/* Tier headline */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18 }}>
        <div>
          <p style={{ margin: 0, fontSize: '9pt', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#a16207', fontWeight: 700 }}>
            {tier.tagline}
          </p>
          <h2 style={{ margin: '2pt 0 0 0', fontSize: '18pt', fontWeight: 800 }}>{tier.subtitle}</h2>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: '26pt', fontWeight: 900, lineHeight: 1 }}>{tier.amountLabel}</p>
          <p style={{ margin: '2pt 0 0 0', fontSize: '10pt', color: '#525252' }}>{tier.duration}</p>
        </div>
      </div>

      {/* Narrative */}
      <p style={{ fontSize: '11pt', lineHeight: 1.55, color: '#262626', marginBottom: 18 }}>{tier.narrative}</p>

      {/* Ensemble + Deliverables side-by-side */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 22 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '10pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a16207', marginBottom: 6 }}>
            Ensemble
          </h3>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: '10.5pt', lineHeight: 1.55 }}>
            {tier.ensemble.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '10pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a16207', marginBottom: 6 }}>
            Deliverables
          </h3>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: '10.5pt', lineHeight: 1.55 }}>
            {tier.deliverables.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      </div>

      {/* Budget table */}
      <h3 style={{ fontSize: '10pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a16207', borderTop: '2px solid #eab308', paddingTop: 12 }}>
        Line-Item Budget (USD)
      </h3>
      <div style={{ marginTop: 6 }}>
        {tier.budget.map((l, i) => (
          <div key={i} className="row">
            <span>{l.label}</span>
            <span className="amt">${l.amount.toLocaleString()}</span>
          </div>
        ))}
        <div className="row" style={{ background: '#fef3c7', padding: '10pt 8pt', marginTop: 6, borderRadius: 4, borderBottom: 'none' }}>
          <span style={{ fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase', fontSize: '11pt' }}>Total</span>
          <span style={{ fontWeight: 900, fontSize: '15pt' }}>${total.toLocaleString()}</span>
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 32, paddingTop: 12, borderTop: '1px solid #d4d4d4', fontSize: '8.5pt', color: '#737373', textAlign: 'center', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
        Escola de Samba Casa Samba · Est. 1987 · New Orleans · NOTCF Grant Applicant
      </div>
    </div>
  );
}
